import type { IAllocationHistoryExportCmd } from "@/data/commands/table/AllocationHistoryExportCmd";
import type { IBulkTableCmd } from "@/data/commands/table/BulkTableCmd";
import type { ICreateTableCmd, IUpdateTableCombinationsCmd } from "@/data/commands/table/CreateTableCmd";
import type { IDeleteTableCombinationCmd } from "@/data/commands/table/IDeleteTableCombinationCmd";
import type { ISetTableDimensionCmd } from "@/data/commands/table/SetTableDimensionCmd";
import { OperatorDao } from "@/data/dao/service/OperatorDao";
import type { PermalinkMode } from "@/data/model/Enums";
import type { ITableCombination, ITableCombinationTable } from "@/data/model/ITableCombination";
import type { IReservationTablesToAssign } from "@/data/model/Order";
import type { IPermalink } from "@/data/model/Permalink";
import type { IAllocationHistory, ITable, ITableDimension, ITableSplit } from "@/data/model/Table";
import type { IAllocationHistoryQry } from "@/data/queries/table/AllocationHistoryQry";
import type { ITableCombinationsQry } from "@/data/queries/table/ITableCombinationsQry";
import type { ITablesQry } from "@/data/queries/table/TablesQry";
import { CurrentLocationService } from "@/data/services/CurrentLocationService";
import { DateTime } from "@/data/util/DateTime";
import { Misc } from "@/data/util/Misc";

import {
    BaseServiceDao,
    type IDataSource,
    type IEditableDataSource,
    type IMultiEntityLinkDataSource,
    type IPaginatedResult,
    parseCollection,
    parsePaginatedResult,
} from "./BaseServiceDao";
import type {
    IGetAvailableTablesOrCombinationsQry
} from "@/data/queries/operator/GetAvailableTablesOrCombinationsQry";


export class TableDao extends BaseServiceDao implements IEditableDataSource<ITable> {
    public static className = "TableDao";

    constructor() {
        super("table");
        this.isLocationDependent = true;
    }

    // tables of the current location (alphabetically sorted by the server) for a multi entity input
    public tableSearch: IMultiEntityLinkDataSource = {
        search: async (excludeIds, term) => {
            const tables = await this.getData({ term: term, offset: 0, limit: 1000 });

            return tables.items
                .filter((table) => !excludeIds.includes(table.id))
                .map((table) => ({ id: table.id, name: table.name }));
        },
    };

    public async updateTableCombinations(cmd: IUpdateTableCombinationsCmd): Promise<Array<ITableCombination>> {
        return this.post("combination/bulk", cmd,(c) => parseCollection(c, TableDao.parseTableCombination));
    }

    public async deleteTableCombination(cmd: IDeleteTableCombinationCmd): Promise<void> {
        return this.delete("combination/" + cmd.tableCombinationId, cmd);
    }

    public async getTableCombinations(cmd: ITableCombinationsQry): Promise<Array<ITableCombination>> {
        return this.get("combination", cmd,(c) => parseCollection(c, TableDao.parseTableCombination));
    }

    public async getAvailableTablesOrCombinations(
        qry: IGetAvailableTablesOrCombinationsQry
    ): Promise<IReservationTablesToAssign> {
        return this.get("available-tables-or-combinations", qry, OperatorDao.parseAvailableTablesOrCombinations);
    }

    public async getSingle(id: string): Promise<ITable> {
        return this.get(id, undefined, TableDao.parseTable);
    }

    public async getData(qry: ITablesQry): Promise<IPaginatedResult<ITable>> {
        return this.get("", qry, (f) => parsePaginatedResult(f, TableDao.parseTable));
    }

    public async create(cmd: ICreateTableCmd): Promise<ITable> {
        return this.post("", cmd, TableDao.parseTable);
    }

    public async update(id: string, cmd: ICreateTableCmd): Promise<ITable> {
        return this.put(id, cmd, TableDao.parseTable);
    }

    public async remove(id: string): Promise<void> {
        return this.delete(id, {});
    }

    public async bulk(cmd: IBulkTableCmd): Promise<void> {
        return this.post("bulk", cmd);
    }

    public async bulkPreview(
        cmd: IBulkTableCmd
    ): Promise<{ createsTables: number; updatesTables: number; deletesTables: number }> {
        return this.get("bulk/preview", cmd, (c) => {
            return {
                createsTables: parseInt(c.createsTables),
                deletesTables: parseInt(c.deletesTables),
                updatesTables: parseInt(c.updatesTables),
            };
        });
    }

    public async reset(id: string): Promise<ITable> {
        return this.post(id + "/reset", {}, TableDao.parseTable);
    }

    public async deallocateAll(id: string): Promise<void> {
        return this.post(id + "/deallocateall", {});
    }

    public async setPosition(id: string, cmd: ISetTableDimensionCmd): Promise<ITable> {
        return this.put(id + "/dimension", cmd, TableDao.parseTable);
    }

    public async removePosition(id: string): Promise<ITable> {
        return this.delete(id + "/dimension", {}, TableDao.parseTable);
    }

    //permalinks
    public async getPermalink(tableId: string): Promise<IPermalink> {
        return this.get(tableId + "/permalink/", undefined, TableDao.parsePermalink);
    }

    public async createPermalink(tableId: string): Promise<IPermalink> {
        return this.post(tableId + "/permalink/", {}, TableDao.parsePermalink);
    }

    public async removePermalink(tableId: string, permalinkId: string): Promise<void> {
        return this.delete(tableId + "/permalink/" + permalinkId, {});
    }

    public async downloadPermalinkQrCode(tableId: string, mode: PermalinkMode) {
        const locationId = CurrentLocationService.getLocationId();
        location.href =
            this.basePath +
            "/" +
            tableId +
            "/permalink/qrcode?c=" +
            new Date().getTime() +
            (locationId !== undefined ? "&locationId=" + locationId : "") + "&mode=" + mode;
    }

    //table allocation history
    public async getAllocationHistory(
        qry: IAllocationHistoryQry,
        abortSignal?: AbortSignal
    ): Promise<IPaginatedResult<IAllocationHistory>> {
        return this.get(
            "allocation/",
            qry,
            (f) => parsePaginatedResult(f, TableDao.parseAllocationHistory),
            abortSignal
        );
    }

    public allocationHistoryDataSource: IDataSource<IAllocationHistory> = {
        getData: (filter, abortSignal) => this.getAllocationHistory(filter, abortSignal),
    };

    public exportTableAllocationHistory(data: IAllocationHistoryExportCmd) {
        return this.postDownloadFile("allocation/export", data);
    }

    public static parseTableCombination(literal: any): ITableCombination {
        return {
            id: literal.id,
            locationId: literal.locationId,
            tables: parseCollection(literal.tables, TableDao.parseTableCombinationTable),
            sortOrder: literal.sortOrder == null ? undefined : literal.sortOrder,
            preferredMinimumOccupancy: literal.preferredMinimumOccupancy,
            maximumOccupancy: literal.maximumOccupancy,
            isActive: literal.isActive,
            name: literal.name,
        };
    }

    public static parseTableCombinationTable(literal: any): ITableCombinationTable {
        return {
            tableId: literal.tableId,
            order: literal.order,
            name: literal.name,
        };
    }

    public static parseTableSplit(literal: any): ITableSplit {
        return {
            id: literal.id,
            maximumSize: literal.maximumSize,
            preferredMinimumSize: literal.preferredMinimumSize,
        };
    }
    
    public static parseTable(literal: any): ITable {
        return {
            id: literal.id,
            locationId: literal.locationId,
            isActive: literal.isActive,
            name: literal.name,
            dataOrigin: parseInt(literal.dataOrigin),
            posId: literal.posId,
            roomId: literal.roomId,
            roomName: literal.roomName,
            shape: parseInt(literal.shape),
            preferredMinimumOccupancy: parseInt(literal.preferredMinimumOccupancy),
            tableNumber: parseInt(literal.tableNumber),
            requireInfo: literal.requireInfo,
            seats: parseInt(literal.seats),
            dimension: literal.dimension == null ? undefined : TableDao.parseTableDimension(literal.dimension),
            isVisibleOnline: literal.isVisibleOnline,
            allowSeveralSplits: literal.allowSeveralSplits,
            tableSplits: parseCollection(literal.tableSplits, TableDao.parseTableSplit),
            minimumSeatsBetweenParties: literal.minimumSeatsBetweenParties == null ? undefined : literal.minimumSeatsBetweenParties,
            assignmentPriority: literal.assignmentPriority,
        };
    }

    public static parsePermalink(literal: any): IPermalink {
        return {
            id: literal.id,
            code: literal.code,
            locationId: literal.locationId,
            locationName: literal.locationName,
            locationSlug: literal.locationSlug,
            tableId: literal.tableId == null ? undefined : literal.tableId,
            tableName: literal.tableName == null ? undefined : literal.tableName,
            tableNumber: literal.tableNumber == null ? undefined : parseInt(literal.tableNumber),
            tableIsVisibleOnline: literal.tableIsVisibleOnline == null ? undefined : literal.tableIsVisibleOnline,
            created: DateTime.parseIsoDate(literal.created),
        };
    }

    public static parseTableDimension(literal: any): ITableDimension {
        return {
            height: Misc.parseFloat(literal.height, 5)!,
            width: Misc.parseFloat(literal.width, 5)!,
            posX: Misc.parseFloat(literal.posX, 5)!,
            posY: Misc.parseFloat(literal.posY, 5)!,
        };
    }

    public static parseAllocationHistory(literal: any): IAllocationHistory {
        return {
            allocated: DateTime.parseIsoDate(literal.allocated),
            isSignedUp: literal.isSignedUp,
            deallocated: literal.deallocated == null ? undefined : DateTime.parseIsoDate(literal.deallocated),
            firstName: literal.firstName == null ? undefined : literal.firstName,
            lastName: literal.lastName == null ? undefined : literal.lastName,
            phoneNumber: literal.phoneNumber == null ? undefined : literal.phoneNumber,
            zip: literal.zip == null ? undefined : literal.zip,
            birthday: literal.birthday == null ? undefined : DateTime.parseIsoDate(literal.birthday),
            locationId: literal.locationId,
            locationName: literal.locationName,
            tableId: literal.tableId == null ? undefined : literal.tableId,
            tableName: literal.tableName == null ? undefined : literal.tableName,
            isCompleteCovidSignup: literal.isCompleteCovidSignup,
            tableAdditionalInfo: literal.tableAdditionalInfo == null ? undefined : literal.tableAdditionalInfo,
        };
    }
}
